import { useQuery } from "@tanstack/react-query";
import {
  useDataProvider,
  type GetListParams,
  type GetListResult,
  type RaRecord,
  type SortPayload,
} from "ra-core";

/**
 * Every row of a list, page by page (B6.3). The Agencies tab used to ask for
 * one page (100 agencies, 1,000 logins and members) and silently stopped
 * there; PostgREST also caps a single response at the project's max-rows
 * (1,000 on Supabase). This walks the pages instead.
 */
export type FetchListPage<T extends RaRecord> = (
  params: Pick<GetListParams, "pagination" | "sort" | "filter">,
) => Promise<Pick<GetListResult<T>, "data" | "total">>;

export type AllPagesResult<T> = {
  data: T[];
  /** True when `maxPages` ran out before the list did. */
  truncated: boolean;
};

export async function collectAllPages<T extends RaRecord>(
  fetchPage: FetchListPage<T>,
  {
    perPage = 1000,
    maxPages = 50,
    sort,
    filter = {},
  }: {
    perPage?: number;
    maxPages?: number;
    sort?: SortPayload;
    filter?: Record<string, unknown>;
  } = {},
): Promise<AllPagesResult<T>> {
  const seen = new Set<string>();
  const data: T[] = [];
  for (let page = 1; page <= maxPages; page += 1) {
    const result = await fetchPage({
      pagination: { page, perPage },
      sort,
      filter,
    });
    const rows = result.data ?? [];
    for (const row of rows) {
      // A row inserted mid-walk shifts later pages by one: keep the first copy.
      const key = String(row.id);
      if (seen.has(key)) continue;
      seen.add(key);
      data.push(row);
    }
    const total = result.total;
    if (rows.length < perPage) return { data, truncated: false };
    if (typeof total === "number" && page * perPage >= total) {
      return { data, truncated: false };
    }
  }
  return { data, truncated: true };
}

/**
 * `collectAllPages` for one resource, cached by react-query under its own key
 * (`[resource, "getAllPages", …]`). ra-core's `useRefresh` invalidates every
 * query, so the Agencies tab's refresh-after-save keeps working.
 */
export function useAllRecords<T extends RaRecord>(
  resource: string,
  {
    sort,
    filter,
    perPage = 1000,
    enabled = true,
    staleTime,
  }: {
    sort?: SortPayload;
    filter?: Record<string, unknown>;
    perPage?: number;
    enabled?: boolean;
    staleTime?: number;
  } = {},
) {
  const dataProvider = useDataProvider();
  const query = useQuery({
    queryKey: [resource, "getAllPages", { sort, filter, perPage }],
    enabled,
    staleTime,
    queryFn: () =>
      collectAllPages<T>(
        (params) => dataProvider.getList<T>(resource, params),
        { perPage, sort, filter },
      ),
  });
  return {
    data: query.data?.data,
    truncated: query.data?.truncated ?? false,
    error: query.error,
    isPending: enabled && query.isPending,
  };
}
